import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  pipes,
  settings,
  type PipeCardDetail,
  type PipeJustification,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';

/** O movimento pedido: a coluna de destino e o que ela exige. */
export interface PipeMoveTarget {
  stepId: string;
  stepName: string;
  /** Nome da transicao da operacao que o movimento pede, quando pede. */
  operationLabel: string;
  /** A coluna declina a operacao ou exige justificativa. */
  requiresReason: boolean;
  justification: PipeJustification;
}

/**
 * Confirmacao do movimento do cartao, com o motivo e a justificativa da coluna.
 *
 * O movimento acontece no servidor: a coluna ligada a uma etapa da operacao passa
 * pela transicao da operacao, com a alcada e o parecer que ela exige, e a recusa
 * volta aqui com a mensagem do servidor. A tela so diz antes o que o movimento faz.
 */
export function PipeMoveDialog({
  cardId,
  pipeId,
  target,
  onClose,
  onMoved,
}: {
  cardId: string;
  pipeId: string;
  target: PipeMoveTarget | null;
  onClose: () => void;
  onMoved?: (card: PipeCardDetail) => void;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [motivo, setMotivo] = useState('');
  const [justificativa, setJustificativa] = useState('');

  const aceitaJustificativa =
    target !== null &&
    target.justification.can_add &&
    target.justification.allowed_in_step;

  const motivos = useQuery({
    queryKey: ['decline-reasons', 'esteira', pipeId],
    queryFn: ({ signal }) => settings.declineReasons({ pipe_id: pipeId }, signal),
    enabled: target !== null && aceitaJustificativa && can('configuracoes.motivo.view'),
    staleTime: 5 * 60_000,
  });

  const mover = useMutation({
    mutationFn: () =>
      pipes.move(cardId, {
        to_step_id: target?.stepId ?? '',
        decline_reason_id: motivo || null,
        justification_text: justificativa.trim(),
      }),
    onSuccess: (cartao) => {
      toast.success(`Cartao movido para ${cartao.step.name}.`);
      setMotivo('');
      setJustificativa('');
      void queryClient.invalidateQueries({ queryKey: ['pipes'] });
      void queryClient.invalidateQueries({ queryKey: ['pipe-cards'] });
      void queryClient.invalidateQueries({ queryKey: ['operations'] });
      void queryClient.invalidateQueries({ queryKey: ['demands'] });
      onMoved?.(cartao);
      onClose();
    },
  });

  const apiError = mover.error instanceof ApiError ? mover.error : null;
  const faltaMotivo =
    target !== null &&
    target.requiresReason &&
    motivo === '' &&
    justificativa.trim() === '';

  return (
    <Modal
      open={target !== null}
      onClose={onClose}
      title={target ? `Mover para ${target.stepName}` : 'Mover cartao'}
      description={
        target?.operationLabel
          ? `Este movimento pede a transicao da operacao: ${target.operationLabel}. A permissao e o parecer exigidos sao conferidos pelo servidor.`
          : 'O movimento fica no historico do cartao, com quem moveu e quando.'
      }
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            loading={mover.isPending}
            disabled={faltaMotivo}
            onClick={() => mover.mutate()}
          >
            Mover
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {mover.isError && !fieldErrorOf(apiError, 'decline_reason_id') ? (
          <Alert tone="danger" title="Nao foi possivel mover o cartao">
            {mover.error.message}
          </Alert>
        ) : null}

        {aceitaJustificativa ? (
          <>
            {(motivos.data ?? []).length > 0 ? (
              <Select
                label="Motivo padronizado"
                placeholder="Sem motivo padronizado"
                options={(motivos.data ?? []).map((m) => ({
                  value: m.id,
                  label: m.label,
                }))}
                value={motivo}
                error={fieldErrorOf(apiError, 'decline_reason_id')}
                hint="Os motivos que valem nesta esteira."
                onChange={(event) => setMotivo(event.target.value)}
              />
            ) : null}
            <Textarea
              label="Justificativa"
              value={justificativa}
              maxLength={4000}
              required={target?.requiresReason}
              error={
                fieldErrorOf(apiError, 'justification_text') ??
                (target?.requiresReason && !motivos.data?.length
                  ? fieldErrorOf(apiError, 'decline_reason_id')
                  : undefined)
              }
              hint={
                target?.requiresReason
                  ? 'Esta coluna exige o motivo padronizado ou a justificativa.'
                  : 'Opcional. Fica no historico do cartao.'
              }
              onChange={(event) => setJustificativa(event.target.value)}
            />
          </>
        ) : (
          <p className="text-sm text-ink-600">Esta coluna nao aceita justificativa.</p>
        )}
      </div>
    </Modal>
  );
}
